import React from "react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/Tooltip";

// Small presentational component for showing selected locations with per-state tooltips.
export const SelectedLocationsDisplay = React.memo(
  function SelectedLocationsDisplay({ selectedLocations, fallbackState, compact }) {
    if (!Array.isArray(selectedLocations) || selectedLocations.length === 0)
      return <span className="text-gray-500">{fallbackState || "—"}</span>;

    const maxShow = compact ? 2 : selectedLocations.length;
    const showItems = selectedLocations.slice(0, maxShow);
    const restCount = selectedLocations.length - maxShow;

    const items = showItems.map((s, i) => {
      const name = s.state_name || `State ${s.stateid || ""}`;
      const counties = Array.isArray(s.counties) ? s.counties : [];
      const countiesText = counties.length
        ? counties
            .map((c) => c.name || c.countyname || c.county_name || c.id)
            .join(", ")
        : "All counties";
      return (
        <Tooltip key={s.stateid || i}>
          <TooltipTrigger asChild>
            <span className={compact ? "text-xs text-blue-700 underline cursor-help" : "text-sm text-blue-700 underline cursor-help"}>
              {name}
            </span>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>{countiesText}</TooltipContent>
        </Tooltip>
      );
    });

    const interspersed = items.reduce((acc, el, idx) => {
      if (idx === 0) return [el];
      return acc.concat([<span key={`sep-${idx}`}>,&nbsp;</span>, el]);
    }, []);

    const fullListTooltip = selectedLocations
      .map((s) => {
        const name = s.state_name || `State ${s.stateid || ""}`;
        const counties = Array.isArray(s.counties) ? s.counties : [];
        const countiesText = counties.length
          ? counties.map((c) => c.name || c.countyname || c.county_name || c.id).join(", ")
          : "All counties";
        return `${name}: ${countiesText}`;
      })
      .join("\n");

    if (compact && restCount > 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-0.5 flex-wrap max-w-[140px]">
              {interspersed}
              <span className="text-xs text-gray-500 whitespace-nowrap">+{restCount}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent sideOffset={6} className="max-w-xs whitespace-pre-line">
            {fullListTooltip}
          </TooltipContent>
        </Tooltip>
      );
    }

    if (compact) {
      return (
        <div className="flex items-center gap-0.5 flex-wrap max-w-[140px]">
          {interspersed}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 flex-wrap">{interspersed}</div>
    );
  }
);

// Shared personal info fields used in both create and edit modals
export const PersonalFields = React.memo(function PersonalFields({
  form,
  setForm,
  errors,
  onEmailBlur,
  onEmailChange,
}) {
  const formatMobile = (input) => {
    const digits = input.replace(/[^0-9]/g, "").slice(0, 20);
    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i === 3 || i === 6) formatted += " - ";
      formatted += digits[i];
    }
    return formatted;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatMobile(value);
    setForm((s) => ({ ...s, phone: formatted }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          First name
        </label>
        <input
          value={form.firstname}
          onChange={(e) =>
            setForm((s) => ({ ...s, firstname: e.target.value }))
          }
          className="w-full border rounded p-1.5 text-sm"
        />
        {errors.name && (
          <div className="text-red-600 text-xs mt-1">{errors.name}</div>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Last name
        </label>
        <input
          value={form.lastname}
          onChange={(e) => setForm((s) => ({ ...s, lastname: e.target.value }))}
          className="w-full border rounded p-1.5 text-sm"
        />
        {errors.name && (
          <div className="text-red-600 text-xs mt-1">{errors.name}</div>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Email
        </label>
        <input
          value={form.email}
          onChange={(e) => {
            setForm((s) => ({ ...s, email: e.target.value }));
            onEmailChange?.();
          }}
          onBlur={onEmailBlur}
          className="w-full border rounded p-1.5 text-sm"
        />
        {errors.email && (
          <div className="text-red-600 text-xs mt-1">{errors.email}</div>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Phone
        </label>
        <input
          value={form.phone}
          className="w-full border rounded p-1.5 text-sm"
          placeholder="e.g. 555 - 555 - 55555"
          onChange={(e) => handlePhoneChange(e?.target?.value)}
        />
        {errors.phone && (
          <div className="text-red-600 text-xs mt-1">{errors.phone}</div>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Title
        </label>
        <input
          value={form.title}
          onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
          className="w-full border rounded p-1.5 text-sm"
          placeholder="Sales Manager"
        />
        {errors.title && (
          <div className="text-red-600 text-xs mt-1">{errors.title}</div>
        )}
      </div>
    </div>
  );
});

// Multi-select states used in the create modal
export const StatesSelectorMulti = React.memo(function StatesSelectorMulti({
  states,
  modalStateIds,
  onChange,
}) {
  return (
    <select
      value={modalStateIds.map(String)}
      onChange={onChange}
      className="hs-select block w-full border rounded p-2 bg-white text-sm max-h-40 overflow-auto"
    >
      <option value="">Select State</option>
      {states.map((s) => {
        const sid = s.id || s.stateid || s.value;
        return (
          <option key={sid} value={sid}>
            {s.statename || s.name}
          </option>
        );
      })}
    </select>
  );
});

// Checkbox list for states (used in edit modal)
export const StatesCheckboxList = React.memo(function StatesCheckboxList({
  states,
  modalStateIds,
  modalMissing,
  onToggle,
}) {
  return (
    <div className="max-h-40 overflow-auto border rounded p-2 bg-white select-state-inner">
      {states.map((s) => {
        const sid = s.id || s.stateid || s.value;
        const checked = modalStateIds.includes(sid);
        const missingForState =
          modalMissing && modalMissing[sid] && modalMissing[sid].length > 0;
        return (
          <label
            key={sid}
            className={`flex items-center gap-2 text-sm mb-1 ${
              missingForState
                ? "bg-red-50 border border-red-200 rounded p-1"
                : ""
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => onToggle(sid, e.target.checked)}
            />
            <span>{s.statename || s.name}</span>
          </label>
        );
      })}
    </div>
  );
});

// Counties panel renders counties and selected tags for all selected states
export const CountiesPanel = React.memo(function CountiesPanel({
  modalStateIds,
  states,
  modalCountiesMap,
  modalSelectedCounties,
  modalMissing,
  onToggleCounty,
  onRemoveCountyTag,
}) {
  if (!modalStateIds || modalStateIds.length === 0) return;

  return (
    <div className="space-y-3 state-ol">
      {modalStateIds.map((sid) => {
        const sObj = states.find(
          (s) => (s.id || s.stateid || s.value) === sid
        ) || { id: sid };
        const counties = modalCountiesMap[sid] || [];
        const selectedForState = modalSelectedCounties[sid] || [];
        return (
          <div
            key={sid}
            className="border rounded p-2 bg-gray-50 state-ol-inner"
          >
            <div className="flex justify-between items-center mb-2">
              <div className="font-medium text-sm">
                {sObj.statename || sObj.name || `State ${sid}`}
              </div>
              <div className="text-xs text-gray-500">
                {selectedForState.length} selected
              </div>
            </div>
            <div className="max-h-32 overflow-auto">
              {counties.map((c) => {
                const cid = c.id || c.countyid || c.value;
                const checked = (modalSelectedCounties[sid] || []).includes(
                  cid
                );
                const missingForCounty =
                  modalMissing &&
                  Array.isArray(modalMissing[sid]) &&
                  modalMissing[sid].includes(cid);
                return (
                  <label
                    key={cid}
                    className={`flex items-center gap-2 text-sm mb-1 ${
                      missingForCounty
                        ? "bg-red-50 border border-red-200 rounded p-1"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        onToggleCounty(sid, cid, e.target.checked)
                      }
                    />
                    <span>{c.countyname || c.name}</span>
                  </label>
                );
              })}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(modalSelectedCounties[sid] || []).map((cid) => {
                const cArr = modalCountiesMap[sid] || [];
                const cObj = cArr.find(
                  (c) => (c.id || c.countyid || c.value) === cid
                ) || { id: cid };
                return (
                  <div
                    key={cid}
                    className="flex items-center gap-2 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                  >
                    <span>
                      {cObj.countyname || cObj.name || `County ${cid}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemoveCountyTag(sid, cid)}
                      className="font-bold"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
});

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  SelectedLocationsDisplay,
  PersonalFields,
  StatesSelectorMulti,
  StatesCheckboxList,
  CountiesPanel,
};
